import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import Typography from "@/ui/Typography/Typography";
import Dropdown from "@/ui/Dropdown/Dropdown";
import { FlexRow, FlexColumn } from "@/ui/FlexContainer/FlexContainer";
import { useChatDataContext } from "@/contexts/ChatContext";
import { useUserDataContext } from "@/contexts/UserContext";
import { useAuthStore } from "@/store/auth";
import { ChatMessageI } from "@/api/chat";
import { getPublicChatUsername } from "@/features/Chat/chatIdentity";
import { useSurveysImagesStore } from "@/store/surveysImages";
import { ChatSurveyModal } from "@/ui/Modal/ChatSurveyModal";
import cls from "./styles.module.scss";

function formatTime(dateStr: string) {
  const date = new Date(dateStr);
  return date.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
  });
}

interface Props {
  message: ChatMessageI;
  index: number;
  isHighlighted?: boolean;
  onReplyPreviewClick?: (messageId: number) => void;
}

export default function ChatMessage({
  message,
  index,
  isHighlighted = false,
  onReplyPreviewClick,
}: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { userData } = useUserDataContext();
  const isAuthenticated = useAuthStore((state) =>
    Boolean(state.tokenResponse?.access)
  );
  const { startEdit, startReply, remove, ban } = useChatDataContext();

  const surveysImages = useSurveysImagesStore((state) => state.surveysImages);
  const [isChatSurveyModalOpen, setIsChatSurveyModalOpen] = useState(false);

  const handleBan = () => {
    if (message.user_id) void ban(message.user_id);
  };

  const showMenu = isAuthenticated && !message.is_deleted;

  return (
    <div
      id={`chat-message-${message.id}`}
      className={`${cls.bubbleRow} ${isHighlighted ? cls.highlighted : ""}`}
    >
      <div
        className={[
          cls.bubble,
          message.is_own ? cls.bubbleOwn : cls.bubbleForeign,
          message.is_deleted ? cls.bubbleDeleted : "",
          index === 0 ? cls.bubbleFirst : "",
        ]
          .filter(Boolean)
          .join(" ")}
      >
        {!message.is_own && index === 0 && (
          <div className={cls.nameRow}>
            <Typography
              size="sm"
              weight="medium"
              color="var(--text-dark-primary)"
            >
              {getPublicChatUsername(message.username)}
            </Typography>
          </div>
        )}

        {message.reply_to && (
          <button
            type="button"
            className={cls.replyPreview}
            onClick={() => onReplyPreviewClick?.(message.reply_to!.id)}
          >
            <span className={cls.replyPreviewAuthor}>
              {getPublicChatUsername(message.reply_to.username)}
            </span>
            <span className={cls.replyPreviewText}>
              {message.reply_to.content_label}
            </span>
          </button>
        )}

        <FlexRow alignItems="flex-start" justifyContent="space-between" gap={8}>
          <FlexColumn>
            {message.text && (
              <div className={cls.textCol}>
                <Typography size="sm">
                  {message.is_deleted ? t("chat.messageDeleted") : message.text}
                </Typography>
              </div>
            )}

            {message.survey_response && (
              <div className={cls.textCol}>
                {message.is_deleted ? (
                  <Typography size="sm">{t("chat.messageDeleted")}</Typography>
                ) : (
                  <FlexColumn
                    gap={8}
                    alignItems="center"
                    className={cls.surveyMessage}
                  >
                    <Typography
                      size="sm"
                      lineHeight="22px"
                      color="var(--text-dark-secondary)"
                    >
                      Посмотри мою анкету с{" "}
                      <span style={{ fontWeight: 500 }}>
                        {message.survey_response.pet.name}
                      </span>
                    </Typography>
                    <img
                      src={
                        surveysImages.find(
                          (item) =>
                            item.id === message.survey_response?.survey.id
                        )?.imgActive
                      }
                      alt=""
                    />
                    <Typography
                      size="2xl"
                      family="Manrope"
                      weight="bold"
                      color="var(--text-dark-primary)"
                      lineHeight="32px"
                    >
                      {message.survey_response.survey.title}
                    </Typography>
                    <ChatSurveyModal
                      isOpen={isChatSurveyModalOpen}
                      onCancel={() => setIsChatSurveyModalOpen(false)}
                      img={
                        surveysImages.find(
                          (item) =>
                            item.id === message.survey_response?.survey.id
                        )?.imgActive
                      }
                      survey_response={message.survey_response}
                    />
                    <button
                      type="button"
                      onClick={() => setIsChatSurveyModalOpen(true)}
                      className={cls.surveyMessageBtn}
                    >
                      Перейти
                    </button>
                  </FlexColumn>
                )}
              </div>
            )}

            {message.pet && (
              <button
                type="button"
                className={cls.petMessage}
                onClick={() => navigate(`/pets/${message.pet!.id}`)}
              >
                <img
                  src={message.pet.image || "/images/user_placeholder.png"}
                  alt=""
                  className={cls.petMessageImage}
                />
                <span className={cls.petMessageContent}>
                  <span className={cls.petMessageHint}>Страница питомца</span>
                  <span className={cls.petMessageName}>{message.pet.name}</span>
                  <span className={cls.petMessageLink}>Открыть страницу →</span>
                </span>
              </button>
            )}
          </FlexColumn>

          {showMenu && (
            <Dropdown
              className={cls.messageDropdown}
              opener={
                <button
                  type="button"
                  className={cls.messageMenuButton}
                  aria-label="Действия с сообщением"
                >
                  ⋮
                </button>
              }
              side={message.is_own ? "left" : "right"}
            >
              <FlexColumn gap={4}>
                <button
                  type="button"
                  className={cls.menuItem}
                  onClick={() => startReply(message)}
                >
                  Ответить
                </button>
                {message.is_own && message.text && (
                  <button
                    type="button"
                    className={cls.menuItem}
                    onClick={() => startEdit(message)}
                  >
                    {t("common.edit")}
                  </button>
                )}
                {(message.is_own || userData?.is_staff) && (
                  <button
                    type="button"
                    className={cls.menuItemDanger}
                    onClick={() => void remove(message.id)}
                  >
                    {t("common.delete")}
                  </button>
                )}
                {!message.is_own && userData?.is_staff && (
                  <button
                    type="button"
                    className={cls.menuItemDanger}
                    onClick={handleBan}
                  >
                    {t("chat.banUser")}
                  </button>
                )}
              </FlexColumn>
            </Dropdown>
          )}
        </FlexRow>

        <div className={message.is_own ? cls.metaRowOwn : cls.metaRowForeign}>
          <Typography size="xs" color="var(--text-dark-secondary)">
            {formatTime(message.created_at)}
          </Typography>
        </div>
      </div>
    </div>
  );
}
